<template>
  <div class="sync-page">
    <div class="page-header mb-4">
      <div class="flex align-items-center justify-content-between">
        <div>
          <h1 class="text-900 mb-1">Синхронизация с PartsSoft</h1>
          <p class="text-600 mb-0">Управление категориями, атрибутами и синхронизацией товаров</p>
        </div>
        <div class="flex gap-2 align-items-center flex-wrap">
          <!-- Селектор магазина -->
          <div class="flex align-items-center gap-2">
            <label for="store-selector" class="text-sm text-600 white-space-nowrap">Магазин:</label>
            <Dropdown
              id="store-selector"
              v-model="selectedStoreId"
              :options="stores"
              optionLabel="name"
              optionValue="id"
              placeholder="Выберите магазин"
              :loading="loadingStores"
              class="w-15rem"
              @change="onStoreChange"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value" class="flex align-items-center gap-2">
                  <span>{{ getLocaleFlag(stores.find(s => s.id === slotProps.value)?.locale) }}</span>
                  <i class="pi pi-shop"></i>
                  <span>{{ stores.find(s => s.id === slotProps.value)?.name }}</span>
                </div>
                <span v-else>{{ slotProps.placeholder }}</span>
              </template>
              <template #option="slotProps">
                <div class="flex align-items-center gap-2">
                  <span>{{ getLocaleFlag(slotProps.option.locale) }}</span>
                  <i class="pi pi-shop"></i>
                  <div>
                    <div class="font-medium">
                      {{ slotProps.option.name }}
                      <span class="text-xs text-500 ml-2">{{ getLocaleName(slotProps.option.locale) }}</span>
                    </div>
                    <div class="text-sm text-600">{{ slotProps.option.description }}</div>
                  </div>
                </div>
              </template>
            </Dropdown>
          </div>

          <!-- Кнопка управления магазинами -->
          <Button
            label="⚙️ Магазины"
            icon="pi pi-cog"
            class="p-button-secondary"
            @click="showStoresDialog = true"
          />

          <Button
            :label="isSyncing ? 'В процессе' : 'Прогресс синхронизации'"
            :icon="isSyncing ? 'pi pi-spin pi-spinner' : 'pi pi-chart-line'"
            :class="isSyncing ? 'p-button-info' : 'p-button-secondary'"
            :disabled="!selectedStoreId || isCleaningCategories"
            @click="showProgressDialog = true"
          />
        </div>
      </div>
    </div>
    
    <!-- Модальное окно прогресса синхронизации -->
    <SyncProgressDialog
      v-if="selectedStoreId"
      v-model:visible="showProgressDialog"
      :store-id="selectedStoreId"
      :is-admin="isAdmin"
      @completed="onSyncCompleted"
    />

    <!-- Диалог управления магазинами -->
    <StoresDialog
      v-model="showStoresDialog"
      @store-updated="onStoreUpdated"
    />

    <!-- Вкладки -->
    <TabView v-if="selectedStoreId" v-model:activeIndex="activeTab" @tab-change="onTabChange">
      <!-- Вкладка "Категории" -->
      <TabPanel header="Категории">
        <CategoriesTab 
          :store-id="selectedStoreId" 
          :key="`cat-${selectedStoreId}`"
          @cleanup-status-change="onCleanupStatusChange"
        />
      </TabPanel>

      <!-- Вкладка "Атрибуты" -->
      <TabPanel header="Атрибуты">
        <AttributesTab :store-id="selectedStoreId" :key="`attr-${selectedStoreId}`" />
      </TabPanel>

      <!-- Вкладка "Товары" -->
      <TabPanel header="Товары">
        <ProductsTab 
          :store-id="selectedStoreId" 
          :key="`prod-${selectedStoreId}`" 
          :is-store-syncing="isSyncing"
          @sync-status-change="onChildSyncStatusChange"
        />
      </TabPanel>

      <!-- Вкладка "Отчеты" -->
      <TabPanel header="Отчеты">
        <ReportsTab :store-id="selectedStoreId" :key="`rep-${selectedStoreId}`" />
      </TabPanel>
    </TabView>
    
    <!-- Плейсхолдер если магазин не выбран -->
    <div v-else class="text-center p-6">
      <i class="pi pi-shop text-6xl text-400 mb-4"></i>
      <h3 class="text-600">Выберите магазин для начала работы</h3>
      <p class="text-500">Используйте селектор в правом верхнем углу</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import api from '../services/apiService'

import CategoriesTab from '../components/sync/CategoriesTab.vue'
import AttributesTab from '../components/sync/AttributesTab.vue'
import ProductsTab from '../components/sync/ProductsTab.vue'
import ReportsTab from '../components/sync/ReportsTab.vue'
import SyncProgressDialog from '../components/sync/SyncProgressDialog.vue'
import StoresDialog from '../components/StoresDialog.vue'

const toast = useToast()
const confirm = useConfirm()
const activeTab = ref(0)
const showProgressDialog = ref(false)
const isSyncing = ref(false)
const isCleaningCategories = ref(false)
const showStoresDialog = ref(false)

// Проверка роли для скрытия кнопок pause/resume/stop
const isAdmin = computed(() => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
    return userInfo.role === 'admin'
  } catch { return false }
})

// Управление магазинами
const stores = ref([])
const selectedStoreId = ref(null)
const loadingStores = ref(false)

// Загрузка списка магазинов
const loadStores = async () => {
  loadingStores.value = true
  try {
    const response = await api.get('/stores')
    stores.value = response.data
    
    // Восстановить выбранный магазин из sessionStorage
    const savedStoreId = sessionStorage.getItem('selected_store_id')
    if (savedStoreId && stores.value.find(s => s.id === parseInt(savedStoreId))) {
      selectedStoreId.value = parseInt(savedStoreId)
    } else {
      // Выбрать магазин по умолчанию (is_default = true)
      const defaultStore = stores.value.find(s => s.is_default)
      if (defaultStore) {
        selectedStoreId.value = defaultStore.id
      } else if (stores.value.length > 0) {
        // Если нет дефолтного, берём первый
        selectedStoreId.value = stores.value[0].id
      }
      
      if (selectedStoreId.value) {
        sessionStorage.setItem('selected_store_id', selectedStoreId.value)
      }
    }
  } catch (error) {
    console.error('Ошибка загрузки магазинов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить список магазинов',
      life: 5000
    })
  } finally {
    loadingStores.value = false
  }
}

// Обработчик смены магазина
const onStoreChange = () => {
  sessionStorage.setItem('selected_store_id', selectedStoreId.value)
  // Проверить, идет ли синхронизация для нового магазина
  checkIfSyncing()
  toast.add({
    severity: 'info',
    summary: 'Магазин изменен',
    detail: `Выбран магазин: ${stores.value.find(s => s.id === selectedStoreId.value)?.name}`,
    life: 2000
  })
}

// Проверка, идет ли синхронизация
const checkIfSyncing = async () => {
  if (!selectedStoreId.value) return

  try {
    const response = await api.get(`/sync/products/is-syncing/${selectedStoreId.value}`)
    isSyncing.value = response.data.is_syncing
  } catch (error) {
    console.error('Ошибка проверки статуса синхронизации:', error)
    isSyncing.value = false
  }
}

// Утилиты для работы с языками
const getLocaleFlag = (locale) => {
  const flags = {
    'ru': '🇷🇺',
    'en': '🇬🇧',
    'it': '🇮🇹',
    'de': '🇩🇪',
    'es': '🇪🇸'
  }
  return flags[locale] || '🌍'
}

const getLocaleName = (locale) => {
  const names = {
    'ru': 'Русский',
    'en': 'English',
    'it': 'Italiano',
    'de': 'Deutsch',
    'es': 'Español'
  }
  return names[locale] || locale
}

// Computed для текущего магазина
const currentStore = computed(() => {
  return stores.value.find(s => s.id === selectedStoreId.value)
})

// Сохранение активной вкладки в sessionStorage
const onTabChange = (event) => {
  sessionStorage.setItem('sync_active_tab', event.index)
}

// Восстановление активной вкладки
onMounted(async () => {
  await loadStores()
  
  const savedTab = sessionStorage.getItem('sync_active_tab')
  if (savedTab !== null) {
    activeTab.value = parseInt(savedTab)
  }
  
  // Проверить статус синхронизации при загрузке
  if (selectedStoreId.value) {
    await checkIfSyncing()
  }
})

// Обработчик изменения статуса синхронизации из дочернего компонента
const onChildSyncStatusChange = (status) => {
  isSyncing.value = status
}

// Обработчик изменения статуса очистки пустых категорий
const onCleanupStatusChange = (status) => {
  isCleaningCategories.value = status
}

// Обработчик завершения синхронизации
const onSyncCompleted = (progress) => {
  isSyncing.value = false
  
  if (progress?.stage === 'COMPLETED') {
    const totalSuccess = (progress.products_sequential?.success || 0) + (progress.products_batch?.success || 0)
    const totalFailed = (progress.products_sequential?.failed || 0) + (progress.products_batch?.failed || 0)
    
    toast.add({
      severity: 'success',
      summary: 'Синхронизация завершена',
      detail: `Синхронизировано: ${totalSuccess} товаров. Ошибок: ${totalFailed}`,
      life: 5000
    })
  } else if (progress?.stage === 'FAILED') {
    toast.add({
      severity: 'error',
      summary: 'Ошибка синхронизации',
      detail: progress.message || 'Синхронизация прервана',
      life: 5000
    })
  }
}

// Обработчик обновления переводов

// Обработчик обновления магазинов (после создания/редактирования/удаления)
const onStoreUpdated = async () => {
  await loadStores()
  toast.add({
    severity: 'success',
    summary: 'Магазины обновлены',
    detail: 'Список магазинов успешно обновлён',
    life: 2000
  })
}
</script>

<style scoped>
.sync-page {
  padding: 1rem;
}

.page-header {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

:deep(.p-tabview) {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

:deep(.p-tabview-nav) {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

:deep(.p-tabview-panels) {
  padding: 1.5rem;
}

/* Стили для селектора магазина */
:deep(.p-dropdown) {
  min-width: 200px;
}

:deep(.p-dropdown-label) {
  display: flex;
  align-items: center;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .sync-page .page-header .flex.align-items-center.justify-content-between {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.75rem;
  }
  .sync-page .page-header p {
    display: none;
  }
  .sync-page :deep(.p-dropdown),
  .sync-page .w-15rem {
    width: 100% !important;
  }
  :deep(.p-tabview-panels) {
    padding: 0.75rem;
  }
}
</style>
